import * as Sentry from '@sentry/react'

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENV = import.meta.env.VITE_SENTRY_ENV as string | undefined
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined

// PII 스크럽 패턴 — Sentry로 보내는 컨텍스트에서 사용자 식별 정보 자동 제거
const PII_PATTERNS = [
  /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i, // email
  /01[016789][- ]?\d{3,4}[- ]?\d{4}/, // 한국 휴대폰
]

const PII_REDACTED = '[REDACTED]'

function containsPII(value: unknown): boolean {
  if (typeof value !== 'string') return false
  return PII_PATTERNS.some((pattern) => pattern.test(value))
}

function scrubRecord(record: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!record) return record
  const cleaned: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(record)) {
    cleaned[key] = containsPII(value) ? PII_REDACTED : value
  }
  return cleaned
}

// iOS WKWebView teardown race — 네이티브가 script message handler 를 제거한 뒤
// flutter_inappwebview 내부 JS 의 postMessage 가 호출되면 InvalidAccessError 가
// unhandled rejection 으로 남는다 (앱 동작 영향 없음, ONL-WEB-GACHA-2).
// 앱 코드 프레임이 전혀 없는 네이티브 postMessage 단일 프레임만 정밀 매칭해 drop —
// useBridge 를 거친 실제 브릿지 실패는 `[Bridge] ...` Error 로 래핑되어 살아남는다.
function isNativePostMessageNoise(event: Sentry.ErrorEvent): boolean {
  const exception = event.exception?.values?.[0]
  if (exception?.type !== 'InvalidAccessError') return false

  const frames = exception.stacktrace?.frames
  if (frames?.length !== 1) return false

  return frames[0].function === 'postMessage' && frames[0].filename === '[native code]'
}

export function initSentry(): void {
  // DSN 없으면 no-op (dev/local 검증용)
  if (!DSN) return

  Sentry.init({
    dsn: DSN,
    environment: ENV ?? 'dev',
    release: RELEASE,

    // 에러 100% 캡처 (트래픽이 폭증하면 sampleRate 조절)
    sampleRate: 1.0,

    // performance monitoring 비활성화 (필요 시 활성화)
    tracesSampleRate: 0,

    // 노이즈 차단
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection captured',
      /Loading chunk \d+ failed/,
    ],

    beforeSend(event) {
      if (isNativePostMessageNoise(event)) return null
      if (event.extra) {
        event.extra = scrubRecord(event.extra)
      }
      if (event.contexts) {
        for (const [key, ctx] of Object.entries(event.contexts)) {
          if (ctx && typeof ctx === 'object') {
            event.contexts[key] = scrubRecord(ctx as Record<string, unknown>)
          }
        }
      }
      return event
    },
  })
}

export function setSentryUser(userId: string | null): void {
  if (!DSN) return
  // userId만 보냄 — 이메일·전화·이름 절대 X
  Sentry.setUser(userId ? { id: userId } : null)
}

export { Sentry }
